import { ApiService } from "./api.service";
import { ClientModel, CreateClientModel, CreateVdiClientModel } from "../models/clients/client.model";
import { IResponse } from "../models/response";
import { UsersModel } from "../models/users/users.model";
import { useQuery, useMutation, MutationFunction } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ErrorTypeModel } from "../models/error/error.model";

/**
 * Hook to fetch a paginated list of all clients.
 *
 * @param {number} [page=1] - Page number for pagination.
 * @param {number} [itemsPerPage=10] - Number of clients per page.
 * @param {Record<string, unknown>} [filters] - Optional filters.
 * @param {Record<string, unknown>} [sort] - Optional sorting options.
 * @returns {Object} A React Query object with clients list and a `refetch` function.
 */
export const useFetchClients = (

  query: string = "",  //add query to search
  page = 1,
  itemsPerPage = 10,
  filters?: Record<string, unknown>,
  sort?: Record<string, unknown>
) => {

  const queryKey = [
    "clients",
    page,
    query,
    itemsPerPage,
    JSON.stringify(filters),
    sort,
  ];

  const { refetch, ...queryResult } = useQuery<
    IResponse<ClientModel[]>,
    AxiosError
  >(queryKey, async () => {
    const params: Record<string, unknown> = {
      query: query || "",
      page: page,
      per_page: itemsPerPage,
      sortField: sort?.field,
      sortDirection: sort?.sort,
      ...filters,
    };

    const clientsData = await ApiService.get("/clients", { params });
    return clientsData.data;
  });

  return { ...queryResult, refetch };
};

/**
 * Hook to fetch details of a single client by ID.
 *
 * @param {string} [id] - Client ID.
 * @returns {Object} A React Query object with client details and a `refetch` function.
 */
export const useFetchClient = (id?: string) => {
  const queryKey = ["clients", id];

  const { refetch, ...queryResult } = useQuery<
    IResponse<ClientModel>,
    AxiosError
  >(queryKey, () => ApiService.get(`/clients/${id}`), {
    staleTime: 60 * 1000,
    enabled: Boolean(id),
  });
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch the list of active clients.
 *
 * @returns {Object} A React Query object with active clients data.
 */
export const useFetchActiveClients = () => {
  const queryKey = ["active-clients"];

  return useQuery<IResponse<UsersModel[]>, AxiosError>(queryKey, () =>
    ApiService.get("/get-active-clients")
  );
};


/**
 * Hook to create a new client.
 *
 * @returns {Object} A React Query mutation object to create a client using FormData.
 */
export const useCreateClient = () => {
  const createClient = useMutation<
    IResponse<CreateClientModel>,
    AxiosError<ErrorTypeModel>,
    FormData
  >(async (formData: FormData) => {
    // Make the API request using the formData
    const response = await ApiService.postFormData(`/clients`, formData);
    return response.data;
  });
  return createClient;
};

/**
 * Hook to create a new vdi client.
 *
 * @returns {Object} A React Query mutation object to create a client using FormData.
 */
export const useCreateVDIClient = () => {
  const createClient = useMutation<
    IResponse<CreateVdiClientModel>,
    AxiosError<ErrorTypeModel>,
    FormData
  >(async (formData: FormData) => {
    // Make the API request using the formData
    const response = await ApiService.postFormData(`/vdi-clients`, formData);
    return response.data;
  });
  return createClient;
};

/**
 * Hook to upload multiple clients via an Excel file.
 *
 * @returns {Object} A React Query mutation object to import clients from an Excel file.
 */
export const useUploadClientsExcel = () => {
  const createClients: MutationFunction<
    IResponse<CreateClientModel>,
    FormData
  > = async (formData) => {
    // Make the API request using the formData
    const response = await ApiService.postFormData(`/clients/import`, formData);
    return response.data;
  };
  return useMutation(createClients);
};

/**
 * Hook to update an existing client's information.
 *
 * @param {string} id - Client ID.
 * @returns {Object} A React Query mutation object to update client data using FormData.
 */
export const useUpdateClient = (id: string) => {
  const updateClient = useMutation<
    IResponse<CreateClientModel>,
    AxiosError<ErrorTypeModel>,
    FormData
  >(async (formData: FormData) => {
    const response = await ApiService.postFormData(
      `/clients/${id}?_method=PATCH`,
      formData
    );
    return response;
  });
  return updateClient;
};
